import { useMemo, useRef, useState, type RefObject } from "react";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { Plus } from "lucide-react";
import {
  Badge,
  Button,
  Checkbox,
  DialogClose,
  FeedbackState,
  Field,
  FormDrawer,
  MultiSelect,
  SearchInput,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableIdentity,
  TableRow,
} from "@h2j-data-team/ui";
import type { QlikApp } from "@repo/shared";

import { toast } from "@/components/shared";
import { supportCode } from "@/components/error-screen";
import { getErrorMessage } from "@/lib/api-client";
import { discardMessages, pickerMessages } from "@/lib/ui-labels";
import { useAddGroupApp } from "@/features/groups/api";
import {
  connectionsQueryOptions,
  connectionAppsQueryOptions,
} from "@/features/qlik-connections/api";

/**
 * The selection cell of the library's DataTable (fixed width, a checkbox that fits the row),
 * for the tables composed from the Table primitives here, which have no such variant.
 */
const SELECT_CELL = "h2j-data-table__select";

interface GroupAddAppsDrawerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  groupId: string;
  existingAppIds: string[];
  /** The control that opened the drawer: focus goes back to it on close. */
  restoreFocusRef?: RefObject<HTMLElement | null>;
}

/** Picks Qlik apps of a connection and adds them to this group, one request per app. */
export function GroupAddAppsDrawer({
  open,
  onOpenChange,
  groupId,
  existingAppIds,
  restoreFocusRef,
}: GroupAddAppsDrawerProps) {
  const { t } = useTranslation("sync-groups");
  const { t: tc } = useTranslation("common");
  const [selectedConnectionId, setSelectedConnectionId] = useState<string>("");
  const [selectedApps, setSelectedApps] = useState<Set<string>>(new Set());
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedSpaces, setSelectedSpaces] = useState<string[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const isSubmittingRef = useRef(false);

  const addAppMutation = useAddGroupApp();

  // Fetch available connections
  const connectionsQuery = useQuery({
    ...connectionsQueryOptions,
    enabled: open,
  });
  const connections = connectionsQuery.data;

  // Fetch apps from selected connection
  const appsQuery = useQuery({
    ...connectionAppsQueryOptions(selectedConnectionId),
    enabled: open && !!selectedConnectionId,
  });
  const apps = appsQuery.data;

  const spaceOptions = useMemo(() => {
    if (!apps) return [];
    const spaces = new Set<string>();
    apps.forEach((app: QlikApp) => {
      if (app.spaceName) spaces.add(app.spaceName);
    });
    return Array.from(spaces)
      .sort()
      .map((space) => ({ label: space, value: space }));
  }, [apps]);

  // Filter apps: exclude already added ones and apply search + space filter
  const filteredApps = useMemo(() => {
    if (!apps) return [];
    return apps.filter((app: QlikApp) => {
      const isNotAlreadyAdded = !existingAppIds.includes(app.id);
      const matchesSearch =
        searchQuery === "" ||
        app.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        app.description?.toLowerCase().includes(searchQuery.toLowerCase());
      const matchesSpace =
        selectedSpaces.length === 0 || (app.spaceName && selectedSpaces.includes(app.spaceName));
      return isNotAlreadyAdded && matchesSearch && matchesSpace;
    });
  }, [apps, existingAppIds, searchQuery, selectedSpaces]);

  const handleToggleApp = (appId: string) => {
    setSelectedApps((prev) => {
      const next = new Set(prev);
      if (next.has(appId)) {
        next.delete(appId);
      } else {
        next.add(appId);
      }
      return next;
    });
  };

  const handleSelectAll = () => {
    if (selectedApps.size === filteredApps.length) {
      setSelectedApps(new Set());
    } else {
      setSelectedApps(new Set(filteredApps.map((app: QlikApp) => app.id)));
    }
  };

  const handleAddApps = async () => {
    // Use ref for synchronous guard to prevent double-click/double-render issues
    if (!apps || selectedApps.size === 0 || isSubmittingRef.current) return;

    isSubmittingRef.current = true;
    setIsSubmitting(true);
    const appsToAdd = apps.filter((app: QlikApp) => selectedApps.has(app.id));

    try {
      for (const app of appsToAdd) {
        await addAppMutation.mutateAsync({
          groupId,
          data: {
            connectionId: selectedConnectionId,
            appId: app.id,
            appName: app.name,
            spaceName: app.spaceName ?? null,
            ownerName: app.ownerName ?? null,
          },
        });
      }
      toast.success(t("addApps.successAdded", { count: appsToAdd.length }));
      handleClose();
    } catch (err) {
      toast.error(getErrorMessage(err, t("addApps.failedAdd")));
    } finally {
      isSubmittingRef.current = false;
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    setSelectedConnectionId("");
    setSelectedApps(new Set());
    setSearchQuery("");
    setSelectedSpaces([]);
    isSubmittingRef.current = false;
    setIsSubmitting(false);
    onOpenChange(false);
  };

  const hasFilter = searchQuery !== "" || selectedSpaces.length > 0;

  const failureDescription = (error: unknown) => {
    const code = supportCode(error, tc("error.supportCode"));
    return code
      ? `${t("addApps.loadFailedDescription")} ${code.label}: ${code.value}`
      : t("addApps.loadFailedDescription");
  };

  return (
    <FormDrawer
      open={open}
      onOpenChange={handleClose}
      size="sm"
      closeLabel={tc("close")}
      title={t("addApps.dialogTitle")}
      description={t("addApps.dialogDescription")}
      restoreFocusRef={restoreFocusRef}
      busy={isSubmitting}
      dirty={selectedApps.size > 0}
      discardConfirmation={discardMessages()}
      footer={
        <>
          <DialogClose asChild>
            <Button variant="outline">{t("addApps.cancel")}</Button>
          </DialogClose>
          <Button onClick={handleAddApps} disabled={selectedApps.size === 0} loading={isSubmitting}>
            {isSubmitting ? (
              t("addApps.adding")
            ) : (
              <>
                <Plus />
                {selectedApps.size > 0
                  ? t("addApps.addAppsCount", { count: selectedApps.size })
                  : t("addApps.addApps")}
              </>
            )}
          </Button>
        </>
      }
    >
      <div className="flex h-full flex-col gap-4">
        {/* Connection Selector */}
        <Field label={t("addApps.connectionLabel")}>
          {(control) => (
            <Select
              value={selectedConnectionId}
              onValueChange={(value) => {
                setSelectedConnectionId(value);
                setSelectedApps(new Set());
                setSearchQuery("");
                setSelectedSpaces([]);
              }}
            >
              <SelectTrigger {...control} loading={connectionsQuery.isLoading}>
                <SelectValue placeholder={t("addApps.connectionPlaceholder")} />
              </SelectTrigger>
              <SelectContent>
                {connectionsQuery.isLoading ? (
                  <div className="px-2 py-4 text-center text-sm text-dim">{tc("loading")}</div>
                ) : connectionsQuery.isError ? (
                  <div className="px-2 py-4 text-center text-sm text-dim">
                    {t("addApps.loadFailedTitle")}
                  </div>
                ) : connections?.length === 0 ? (
                  <div className="px-2 py-4 text-center text-sm text-dim">
                    {t("addApps.noConnections")}
                  </div>
                ) : (
                  connections?.map((conn) => (
                    <SelectItem key={conn.id} value={conn.id}>
                      {conn.name}
                    </SelectItem>
                  ))
                )}
              </SelectContent>
            </Select>
          )}
        </Field>

        {/* Apps List */}
        {selectedConnectionId && (
          <div className="flex min-h-0 flex-1 flex-col gap-3">
            <div className="flex items-center justify-between gap-2">
              <h3 className="text-[13px] font-medium text-ink">{t("addApps.applicationsLabel")}</h3>
              {filteredApps.length > 0 && (
                <Button type="button" variant="ghost" size="compact" onClick={handleSelectAll}>
                  {selectedApps.size === filteredApps.length
                    ? t("addApps.deselectAll")
                    : t("addApps.selectAll")}
                </Button>
              )}
            </div>

            {/* Search */}
            <SearchInput
              value={searchQuery}
              onValueChange={setSearchQuery}
              clearLabel={tc("list.clearSearch")}
              placeholder={t("addApps.searchPlaceholder")}
              aria-label={t("addApps.searchPlaceholder")}
            />

            {/* Space filter */}
            {spaceOptions.length > 1 && (
              <MultiSelect
                aria-label={t("addApps.spaceFilterPlaceholder")}
                options={spaceOptions}
                value={selectedSpaces}
                onValueChange={setSelectedSpaces}
                messages={pickerMessages({ placeholder: t("addApps.spaceFilterPlaceholder") })}
              />
            )}

            {/* Apps List */}
            {appsQuery.isLoading ? (
              <FeedbackState state="loading" title={tc("loading")} />
            ) : appsQuery.isError ? (
              <FeedbackState
                state="error"
                title={t("addApps.loadFailedTitle")}
                description={failureDescription(appsQuery.error)}
                action={
                  <Button variant="outline" size="sm" onClick={() => void appsQuery.refetch()}>
                    {tc("tryAgain")}
                  </Button>
                }
              />
            ) : filteredApps.length === 0 ? (
              <FeedbackState
                state={hasFilter && apps?.length ? "no-results" : "empty"}
                title={
                  apps?.length === 0
                    ? t("addApps.noAppsInConnection")
                    : hasFilter
                      ? t("addApps.noAppsMatchSearch")
                      : t("addApps.allAppsAdded")
                }
              />
            ) : (
              <div className="flex max-h-[calc(100vh-420px)] min-h-[220px] flex-col overflow-hidden rounded-card border border-hairline">
                <Table scrollLabel={t("addApps.applicationsLabel")}>
                  <TableHeader>
                    <TableRow>
                      <TableHead className={SELECT_CELL}>
                        <span className="sr-only">{t("addApps.colSelect")}</span>
                      </TableHead>
                      <TableHead>{t("addApps.colApp")}</TableHead>
                      <TableHead className="w-28">
                        <span className="sr-only">{t("addApps.colStatus")}</span>
                      </TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredApps.map((app: QlikApp) => (
                      <TableRow key={app.id} selected={selectedApps.has(app.id)}>
                        <TableCell className={SELECT_CELL}>
                          <Checkbox
                            id={`add-app-${app.id}`}
                            checked={selectedApps.has(app.id)}
                            onCheckedChange={() => handleToggleApp(app.id)}
                          />
                        </TableCell>
                        <TableCell variant="identity">
                          <label htmlFor={`add-app-${app.id}`} className="block cursor-pointer">
                            <TableIdentity
                              name={app.name}
                              detail={app.spaceName ?? undefined}
                              detailMono={false}
                            />
                          </label>
                        </TableCell>
                        <TableCell>
                          {app.published && (
                            <Badge variant="secondary">{t("addApps.published")}</Badge>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}

            {selectedApps.size > 0 && (
              <p className="text-sm text-dim" aria-live="polite">
                {t("addApps.selected", { count: selectedApps.size })}
              </p>
            )}
          </div>
        )}
      </div>
    </FormDrawer>
  );
}
